/**
 * ncalcToBlocks.js — NCalc `Expression` string → Blockly block tree (S1-P7, task 3-B).
 *
 * The REVERSE of task 3-A's `ncalcGenerator.js`. It parses a persisted NCalc expression back into a
 * Blockly value subtree, rebuilds a whole calc-version's workspace from persisted formulas/constants/
 * brackets, and decides which source (a stored v2 workspace blob vs a fresh rebuild) the editor should
 * load. Verified by ROUND-TRIP against 3-A: `ncalcToBlocks(e)` → `workspaceToFormulas` reproduces `e`.
 *
 * PURE ESM. Imports Blockly ONLY through the cross-env shim `./blocklyCore.js` (NEVER `blockly/core`).
 * NO DOM: every function runs HEADLESS under `node tests/*.mjs` over `new Blockly.Workspace()`.
 *
 * ── AST → blocks (mirrors formulaGraph.js `expressionToGraph`'s AST-walk) ────────────────
 * We reuse the EXPORTED parser primitives `parseExpression` / `tokenize` (no new parser) and
 * `classifyField` (source tagging). The parser AST kinds map 1:1 to blocks:
 *   num  → tax_number (VALUE)              id  → tax_variable (NAME + setTaxSource; off-catalog ⇒ flag)
 *   str  → tax_text   (TEXT)               (single-quoted literal; the parser already held it to the
 *                                           literal grammar, so the field validator is a no-op here)
 *   op   → tax_arith  (OP, A, B)           cmp → tax_compare  (OP, A, B)
 *   func → MIN/MAX = tax_func_min|max variadic (ADD0..ADDn) · ROUND = tax_func_round (X[,N]) ·
 *          IF = tax_func_if (COND, THEN, ELSE) · SUM = tax_func_sum (VALUE ← the iterated expression)
 *
 * A RETIRED construct (SUMITEMS, item_amount/item_rate/item_expense) never reaches this module at all:
 * `parseExpression` throws the MIGRATION sentence for it, which is what makes opening such a stored
 * formula a loud, actionable failure instead of a silent rebuild into something else.
 * Identifiers ALWAYS become tax_variable (even Constant-source ones — a Constant-source variable
 * compiles to its bare name exactly like tax_constant, so the round-trip string is preserved).
 * Standalone `tax_constant` / `tax_bracket` blocks are emitted only at version level.
 */
import Blockly from './blocklyCore.js'
import {
  parseExpression,
  classifyField,
  SUM_FN,
  IFS_FN,
} from '../formulaGraph.js'
import { workspaceToFormulas, workspaceToExpression } from './ncalcGenerator.js'

export const GRAPH_VERSION = 2

const VARIADIC_TYPE = { MIN: 'tax_func_min', MAX: 'tax_func_max' }

// ──────────────────────────────────────────────────────────────────────────────
//  Core: AST node → Blockly value block (recursive)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Build a Blockly value subtree from a parser AST node.
 * @param {Object} workspace  a Blockly workspace (headless or rendered)
 * @param {Object} ast        node from parseExpression ({kind:'num'|'id'|'op'|'cmp'|'func', …})
 * @param {Object} catalog    variable catalog for classifyField (source tagging)
 * @returns {Object} the root value block of the built subtree
 */
function buildSubtree(workspace, ast, catalog) {
  if (ast == null) return null
  switch (ast.kind) {
    case 'num': {
      const b = workspace.newBlock('tax_number')
      b.setFieldValue(ast.value, 'VALUE')
      return b
    }
    case 'str': {
      // The generator's exact inverse — without it, reopening a saved `sex == 'FEMALE'` throws
      // "unexpected AST node" and the formula silently degrades to a rebuild the author never asked for.
      const b = workspace.newBlock('tax_text')
      b.setFieldValue(ast.value, 'TEXT')
      return b
    }
    case 'id': {
      const b = workspace.newBlock('tax_variable')
      b.setFieldValue(ast.name, 'NAME')
      const cls = classifyField(ast.name, catalog)
      b.setTaxSource(cls.source)
      // off-catalog / stale identifier → ⚠ badge (AC11). Guarded: setWarningText is a no-op
      // on some headless block variants but present on the base Block here.
      if (cls.flagged && typeof b.setTaxFlagged === 'function') {
        try { b.setTaxFlagged(true) } catch (_err) { /* not rendered — ignore */ }
      }
      return b
    }
    case 'path': {
      // The exact inverse of ncalcGenerator's emitPath: the CANONICAL text goes into the block's
      // stored state (setTaxPath), and the `▸` spelling on the label is derived from it — so the
      // recompile is byte-identical and chooseFormulaSource keeps the author's layout.
      const b = workspace.newBlock('tax_path')
      b.setTaxPath(ast.name)
      const cls = classifyField(ast.name, catalog)
      if (typeof b.setTaxSource === 'function') b.setTaxSource(cls.source)
      // Only the ROOT can be proven missing from the catalog (classifyField); the segments below it are
      // resolved by the engine against the real form JSON, so they are never flagged from here.
      if (cls.flagged && typeof b.setTaxFlagged === 'function') {
        try { b.setTaxFlagged(true) } catch (_err) { /* not rendered — ignore */ }
      }
      return b
    }
    case 'bool': {
      const b = workspace.newBlock('tax_bool')
      b.setFieldValue(ast.value ? 'TRUE' : 'FALSE', 'VALUE')
      return b
    }
    case 'op': {
      const b = workspace.newBlock('tax_arith')
      b.setFieldValue(ast.op, 'OP')
      connectInput(b, 'A', buildSubtree(workspace, ast.left, catalog))
      connectInput(b, 'B', buildSubtree(workspace, ast.right, catalog))
      return b
    }
    case 'cmp': {
      const b = workspace.newBlock('tax_compare')
      b.setFieldValue(ast.cmp, 'OP')
      connectInput(b, 'A', buildSubtree(workspace, ast.left, catalog))
      connectInput(b, 'B', buildSubtree(workspace, ast.right, catalog))
      return b
    }
    case 'func':
      return buildFunc(workspace, ast, catalog)
    default:
      throw new Error('ncalcToBlocks: unexpected AST node ' + JSON.stringify(ast))
  }
}

function buildFunc(workspace, ast, catalog) {
  const fn = String(ast.fn).toUpperCase()
  const args = Array.isArray(ast.args) ? ast.args : []
  if (fn === 'MIN' || fn === 'MAX') {
    const b = workspace.newBlock(VARIADIC_TYPE[fn])
    // Drive the built-in variadic mutator to the required number of ADD sockets (task 2-A).
    b.itemCount_ = Math.max(2, args.length)
    b.updateShape_()
    args.forEach((a, i) => connectInput(b, 'ADD' + i, buildSubtree(workspace, a, catalog)))
    return b
  }
  if (fn === 'ROUND') {
    const b = workspace.newBlock('tax_func_round')
    connectInput(b, 'X', buildSubtree(workspace, args[0], catalog))
    if (args.length > 1) connectInput(b, 'N', buildSubtree(workspace, args[1], catalog))
    return b
  }
  if (fn === 'IF') {
    const b = workspace.newBlock('tax_func_if')
    connectInput(b, 'COND', buildSubtree(workspace, args[0], catalog))
    connectInput(b, 'THEN', buildSubtree(workspace, args[1], catalog))
    connectInput(b, 'ELSE', buildSubtree(workspace, args[2], catalog))
    return b
  }
  if (fn === IFS_FN) {
    const b = workspace.newBlock('tax_func_ifs')
    // Drive the pair mutator to as many PAIRS as the call carries. `parseExpression` already refused an
    // odd count, so the halving is exact — this never has to invent a missing condition or value.
    b.pairCount_ = Math.max(1, Math.floor(args.length / 2))
    b.updateShape_()
    for (let i = 0; i < b.pairCount_; i += 1) {
      connectInput(b, 'COND' + i, buildSubtree(workspace, args[i * 2], catalog))
      connectInput(b, 'VAL' + i, buildSubtree(workspace, args[i * 2 + 1], catalog))
    }
    return b
  }
  if (fn === SUM_FN) {
    // The exact inverse of emitSum: one socket, no fields, no extraState. The parser already refused
    // a nested SUM and a wrong arity, so nothing here has to invent a missing argument.
    const b = workspace.newBlock('tax_func_sum')
    connectInput(b, 'VALUE', buildSubtree(workspace, args[0], catalog))
    return b
  }
  throw new Error('ncalcToBlocks: unknown function ' + ast.fn)
}

/** Stack `block` under `prev` via the previous↔next statement connectors (guarded for headless). */
function chainNext(prev, block) {
  if (!prev || !block) return
  const pc = prev.nextConnection
  const bc = block.previousConnection
  if (pc && bc) {
    try { pc.connect(bc) } catch (_err) { /* model-only / already-connected edge — ignore */ }
  }
}

/** Pick the FINAL block's result var: `net_income` when a formula produces it, else the last output. */
function pickResultVar(orderedFormulas) {
  const vars = (Array.isArray(orderedFormulas) ? orderedFormulas : [])
    .map((f) => (f && f.outputVar != null ? String(f.outputVar).trim() : ''))
    .filter(Boolean)
  if (vars.indexOf('net_income') !== -1) return 'net_income'
  return vars.length ? vars[vars.length - 1] : 'net_income'
}

/** Connect a child block's output into a parent block's value input by name. */
function connectInput(parent, inputName, child) {
  if (!child) return
  const input = parent.getInput(inputName)
  if (input && input.connection && child.outputConnection) {
    input.connection.connect(child.outputConnection)
  }
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public: single expression → block subtree
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Parse one NCalc expression into a Blockly value subtree on `workspace`.
 * @param {Object} workspace   a Blockly workspace
 * @param {string} expression  an NCalc string (S1-P3 grammar)
 * @param {{catalog?:Object}} [opts]  catalog used for classifyField source tagging (AC11)
 * @returns {?Object} the root value block (null for an empty/blank expression). Throws on malformed input.
 */
export function ncalcToBlocks(workspace, expression, opts = {}) {
  const src = String(expression == null ? '' : expression).trim()
  if (src === '') return null
  const ast = parseExpression(src) // throws on malformed input — caller shows the parse-error banner
  return buildSubtree(workspace, ast, opts.catalog)
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public: whole-version rebuild (mirror of formulaGraph.versionToGraph)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Rebuild the WHOLE calc version onto `workspace` from a `/full` DTO. Persisted values win — the
 * workspace is DERIVED from `formula.expression` / bracket rows / constant values (source of truth),
 * never from any stored canvasGraphJson. One `tax_define` per formula (OUTPUTVAR + appliesTo/evalOrder/
 * notes meta + parsed EXPR subtree), one `tax_constant` per constant, one `tax_bracket` for all rows.
 * Formula ordering mirrors versionToGraph (evalOrder, then formulaID). Per-formula parse errors are
 * isolated (lone define + warning) so one bad expression never aborts the rebuild.
 * @param {Object} workspace
 * @param {Object} full   { version, formulas[], brackets[], constants[] }
 * @param {{catalog?:Object}} [opts]
 * @returns {void}
 */
export function versionToWorkspace(workspace, full, opts = {}) {
  const catalog = opts.catalog
  const f = full || {}
  const formulas = Array.isArray(f.formulas) ? f.formulas : []
  const brackets = Array.isArray(f.brackets) ? f.brackets : []
  const constants = Array.isArray(f.constants) ? f.constants : []

  const LANE_X = 560 // side lane for constants + bracket
  const LANE_STEP = 90

  const ordered = formulas.slice().sort(
    (a, b) => (num(a.evalOrder) - num(b.evalOrder)) || (num(a.formulaID) - num(b.formulaID)),
  )

  // Flow spine: START hat → one tax_define per formula (eval order) → FINAL cap. Built ONLY when
  // there is ≥1 formula so an empty version stays an empty workspace (headless round-trip contract).
  let prev = null
  if (ordered.length) {
    const start = workspace.newBlock('tax_start')
    moveTo(start, 0, 0)
    prev = start
  }

  ordered.forEach((formula, i) => {
    const define = workspace.newBlock('tax_define')
    if (define.getField('OUTPUTVAR')) {
      define.setFieldValue(formula.outputVar != null ? String(formula.outputVar) : 'output_var', 'OUTPUTVAR')
    }
    if (typeof define.setTaxMeta === 'function') {
      define.setTaxMeta({
        appliesToKind: formula.appliesToKind != null ? formula.appliesToKind : 'RULE_CODE',
        appliesToKey: formula.appliesToKey != null ? formula.appliesToKey : '',
        evalOrder: formula.evalOrder != null ? Number(formula.evalOrder) : i,
        notes: formula.notes != null ? formula.notes : null,
      })
    }
    try {
      const root = ncalcToBlocks(workspace, formula.expression, { catalog })
      if (root) define.getInput('EXPR').connection.connect(root.outputConnection)
    } catch (err) {
      // unparseable legacy expression → lone read-only define + hint (mirrors versionToGraph)
      setWarn(define, 'แก้สูตรนี้ไม่ได้ — ตรวจไวยากรณ์ (' + (err && err.message ? err.message : 'parse error') + ')')
    }
    chainNext(prev, define) // stack under START / the previous define (previous↔next statement)
    prev = define
  })

  // FINAL cap — names the output var the engine consumes (net_income if present, else the last one).
  if (ordered.length) {
    const result = workspace.newBlock('tax_result')
    if (result.getField('RESULTVAR')) result.setFieldValue(pickResultVar(ordered), 'RESULTVAR')
    chainNext(prev, result)
  }

  // Constant blocks in a side lane.
  constants.forEach((c, i) => {
    const cb = workspace.newBlock('tax_constant')
    if (cb.getField('CONSTKEY')) cb.setFieldValue(c.constKey != null ? String(c.constKey) : 'CONST_KEY', 'CONSTKEY')
    if (cb.getField('CONSTVALUE')) cb.setFieldValue(num(c.constValue), 'CONSTVALUE')
    moveTo(cb, LANE_X, i * LANE_STEP)
  })

  // One Bracket block built from all bracket rows (one per version; non-compiling).
  if (brackets.length) {
    const bb = workspace.newBlock('tax_bracket')
    const rows = brackets
      .slice()
      .sort((a, b) => (num(a.sortOrder) - num(b.sortOrder)) || (num(a.lowerBound) - num(b.lowerBound)))
      .map((b) => ({
        lowerBound: num(b.lowerBound),
        upperBound: b.upperBound == null ? null : num(b.upperBound),
        rate: num(b.rate),
        sortOrder: num(b.sortOrder),
      }))
    if (typeof bb.taxSetRows === 'function') bb.taxSetRows(rows)
    else if (typeof bb.setBracketRows === 'function') bb.setBracketRows(rows)
    moveTo(bb, LANE_X, constants.length * LANE_STEP + 40)
  }

  // Best-effort tidy in the rendered editor (no-op headless — WorkspaceSvg only).
  if (typeof workspace.cleanUp === 'function') {
    try { workspace.cleanUp() } catch (_err) { /* not rendered — ignore */ }
  }
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public: PER-FORMULA graph source (Formula level) — stored layout vs rebuild
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Decide whether the Formula editor loads the STORED per-formula graph
 * (`TaxRuleFormula.ExpressionGraphJson` = `{graphVersion:2, workspace:{…}}`, keeping the hand-tuned
 * layout) or REBUILDS the tree from the persisted `Expression` (which is always the source of truth).
 *
 * Per-formula counterpart of {@link chooseWorkspaceSource}: the stored graph is honoured ONLY when it
 * recompiles to exactly the persisted expression, so a graph that drifted (or was written by an older
 * editor) can never silently resurrect a stale formula.
 * @param {string|Object|null} expressionGraphJson  the stored blob (string or parsed object)
 * @param {string} expression                       the persisted NCalc expression (source of truth)
 * @returns {{ source:'workspace'|'rebuild', workspace?:Object, reason:string }}
 */
export function chooseFormulaSource(expressionGraphJson, expression) {
  let blob = null
  if (expressionGraphJson != null) {
    if (typeof expressionGraphJson === 'string') {
      const s = expressionGraphJson.trim()
      if (s !== '') {
        try { blob = JSON.parse(s) } catch (_err) { blob = null }
      }
      if (blob == null && s !== '') {
        return { source: 'rebuild', reason: 'expressionGraphJson ไม่ใช่ JSON ที่ parse ได้ — rebuild จาก Expression' }
      }
    } else if (typeof expressionGraphJson === 'object') {
      blob = expressionGraphJson
    }
  }

  if (blob == null || typeof blob !== 'object') {
    return { source: 'rebuild', reason: 'ไม่มี graph ที่เก็บไว้ — สร้างบล็อกจาก Expression' }
  }
  if (Number(blob.graphVersion) !== GRAPH_VERSION || blob.workspace == null || typeof blob.workspace !== 'object') {
    return { source: 'rebuild', reason: 'ไม่ใช่ graph v2 (ขาด graphVersion:2/workspace) — rebuild จาก Expression' }
  }

  let ws
  let recompiled
  try {
    ws = new Blockly.Workspace()
    Blockly.serialization.workspaces.load(blob.workspace, ws)
    recompiled = workspaceToExpression(ws)
  } catch (err) {
    return {
      source: 'rebuild',
      reason: 'โหลด graph v2 ไม่สำเร็จ (' + (err && err.message ? err.message : 'load error') + ') — rebuild',
    }
  }

  if (recompiled.errors.length === 0 && normalizeExpr(recompiled.expression) === normalizeExpr(expression)) {
    return { source: 'workspace', workspace: ws, reason: 'graph ที่เก็บไว้ recompile ตรงกับ Expression — ใช้ layout เดิม' }
  }
  return {
    source: 'rebuild',
    reason: 'graph ที่เก็บไว้ recompile ไม่ตรงกับ Expression — rebuild (Expression คือ source of truth)',
  }
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public: decide stored-workspace vs rebuild (PURE — unit-tested directly)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Decide whether the editor should load the STORED v2 workspace blob (keeping its hand-tuned layout)
 * or REBUILD the workspace from the persisted formulas (expressions are the source of truth).
 *
 *   • canvasGraphJson `{graphVersion:2, workspace:{…}}` whose recompiled formulas MATCH `full.formulas`
 *     (same outputVar→expression set) → `{source:'workspace', workspace}` (the loaded temp workspace).
 *   • null / unparseable, `graphVersion:1` (retired Vue-Flow node/edge blob), non-v2, or a v2 blob that
 *     recompiles to DIFFERENT formulas → `{source:'rebuild'}`.
 * Always returns a human `reason`. PURE: builds a throwaway `new Blockly.Workspace()` to validate v2.
 * @param {string|Object|null} canvasGraphJson  the stored blob (string or already-parsed object)
 * @param {Object} full   { formulas[], … } — the persisted formulas (source of truth)
 * @returns {{ source:'workspace'|'rebuild', workspace?:Object, reason:string }}
 */
export function chooseWorkspaceSource(canvasGraphJson, full) {
  // 1) resolve the blob to an object
  let blob = null
  if (canvasGraphJson != null) {
    if (typeof canvasGraphJson === 'string') {
      const s = canvasGraphJson.trim()
      if (s !== '') {
        try { blob = JSON.parse(s) } catch (_err) { blob = null }
      }
      if (blob == null && s !== '') {
        return { source: 'rebuild', reason: 'canvasGraphJson ไม่ใช่ JSON ที่ parse ได้ — rebuild จากสูตร' }
      }
    } else if (typeof canvasGraphJson === 'object') {
      blob = canvasGraphJson
    }
  }

  if (blob == null || typeof blob !== 'object') {
    return { source: 'rebuild', reason: 'ไม่มี canvas ที่เก็บไว้ (null) — rebuild จากสูตร' }
  }

  const gv = Number(blob.graphVersion)
  if (gv === 1) {
    return { source: 'rebuild', reason: 'graphVersion 1 (บล็อบ Vue-Flow node/edge เดิมที่เลิกใช้) — rebuild' }
  }
  if (gv !== GRAPH_VERSION || blob.workspace == null || typeof blob.workspace !== 'object') {
    return { source: 'rebuild', reason: 'ไม่ใช่ workspace blob v2 (ขาด graphVersion:2/workspace) — rebuild' }
  }

  // 2) v2 blob — load into a throwaway workspace, recompile, compare to the persisted formulas
  let recompiled
  let ws
  try {
    ws = new Blockly.Workspace()
    Blockly.serialization.workspaces.load(blob.workspace, ws)
    recompiled = workspaceToFormulas(ws).formulas
  } catch (err) {
    return { source: 'rebuild', reason: 'โหลด workspace v2 ไม่สำเร็จ (' + (err && err.message ? err.message : 'load error') + ') — rebuild' }
  }

  if (formulasMatch(recompiled, (full && full.formulas) || [])) {
    // Legacy v2 blobs (saved before the flow redesign) have loose defines and NO START/FINAL spine.
    // Force a rebuild for those so the pipeline gets its start/end markers; keep the stored layout
    // only once it already carries the spine (so hand-tuned positions survive later opens).
    const hasFlowSpine = ws.getAllBlocks(false).some((b) => b && b.type === 'tax_start')
    if (!hasFlowSpine && recompiled.length > 0) {
      return {
        source: 'rebuild',
        reason: 'workspace เดิมยังไม่มีบล็อก START/FINAL — สร้างสายใหม่พร้อมจุดเริ่ม/จุดจบ (บันทึกเพื่อเก็บ layout)',
      }
    }
    return {
      source: 'workspace',
      workspace: ws,
      reason: 'workspace v2 ที่เก็บไว้ recompile ตรงกับสูตรที่ persist — ใช้ layout เดิม',
    }
  }
  return {
    source: 'rebuild',
    reason: 'workspace v2 ที่เก็บไว้ recompile ไม่ตรงกับสูตรที่ persist — rebuild (สูตรคือ source of truth)',
  }
}

// ──────────────────────────────────────────────────────────────────────────────
//  Internal helpers
// ──────────────────────────────────────────────────────────────────────────────

/** Compare two formula arrays as an outputVar → normalized-expression SET (order-independent). */
function formulasMatch(a, b) {
  const ma = toExprMap(a)
  const mb = toExprMap(b)
  const ka = Object.keys(ma)
  const kb = Object.keys(mb)
  if (ka.length !== kb.length) return false
  return ka.every((k) => Object.prototype.hasOwnProperty.call(mb, k) && ma[k] === mb[k])
}

function toExprMap(list) {
  const map = {}
  ;(Array.isArray(list) ? list : []).forEach((f) => {
    if (!f) return
    const key = f.outputVar != null ? String(f.outputVar) : ''
    map[key] = normalizeExpr(f.expression)
  })
  return map
}

/**
 * Canonical form for expression comparison — strip whitespace BETWEEN tokens, keep it INSIDE string
 * literals. `a  -  b` and `a-b` are the same formula; `'MARRIED JOINT'` and `'MARRIEDJOINT'` are NOT.
 *
 * Stripping blindly (which was sound while whitespace could never be semantic — pre-S1-P9 there were no
 * literals) makes two DIFFERENT expressions compare equal, so a stored graph whose literal drifted from
 * the persisted Expression by a space passes the very recompile guard that exists to fail it: the editor
 * would load the stale graph, show the author a literal the engine does not run, and write it back on save.
 *
 * "The next quote closes the literal" is the same rule formulaGraph's `tokenize` uses, and it is safe for
 * the same reason: the literal grammar has no backslash and no escape sequence, so a quote cannot appear
 * inside one. An unterminated quote is not salvaged — it is not a legal expression and must not match.
 */
export function normalizeExpr(s) {
  const str = String(s == null ? '' : s)
  let out = ''
  let i = 0
  while (i < str.length) {
    const c = str[i]
    // BOTH quote styles, because `tokenize` now reads both: a stored expression written with `"…"`
    // whose content contains a space must compare by its CONTENT here too, or the drift guard would
    // pass on two literals that are not the same value.
    if (c === "'" || c === '"') {
      const close = str.indexOf(c, i + 1)
      if (close === -1) { out += str.slice(i).replace(/\s+/g, ''); break }
      out += str.slice(i, close + 1) // literal VERBATIM, quotes included — spaces are content here
      i = close + 1
      continue
    }
    if (!/\s/.test(c)) out += c
    i += 1
  }
  return out
}

/** Absolute-position a freshly created top block (fresh blocks start at 0,0). Guarded for headless. */
function moveTo(block, x, y) {
  if (block && typeof block.moveBy === 'function') {
    try { block.moveBy(x, y) } catch (_err) { /* not rendered — ignore */ }
  }
}

function setWarn(block, text) {
  if (!block || typeof block.setWarningText !== 'function') return
  try { block.setWarningText(text) } catch (_err) { /* headless / not rendered — ignore */ }
}

function num(v, d = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : d
}

export default {
  GRAPH_VERSION,
  ncalcToBlocks,
  normalizeExpr,
  versionToWorkspace,
  chooseFormulaSource,
  chooseWorkspaceSource,
}
